import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetPurchaseApplicationSummaryQuery } from '@/pages/mortgage/apis/purchaseAPI';
import {
  useFetchBlueprintLendersQuery,
  useFetchCalculationSettingsQuery,
  useFetchFxRatesQuery,
  useFetchHemBenchmarksQuery,
  useFetchIncomeTaxBracketsQuery,
  useFetchStampDutyBracketsQuery,
} from '@/pages/blueprint-lite/apis/blueprintApi';

import {
  mapSummaryToAssessmentInput,
  runPurchaseAssessment,
} from '../../assessment';
import { normaliseCalculationSettings } from '../../assessment/config/calculationSettings';
import { normaliseTaxBrackets } from '../../assessment/config/taxBrackets';
import BlueprintHeader from './sections/BlueprintHeader';
import CapitalGainsCard from './sections/CapitalGainsCard';
import CashflowCard from './sections/CashflowCard';
import Chapter from './sections/Chapter';
import Disclaimer from './sections/Disclaimer';
import KeyMetrics from './sections/KeyMetrics';
import LendersPanel from './sections/LendersPanel';
import StrengthsConsiderations from './sections/StrengthsConsiderations';
import TaxNote from './sections/TaxNote';
import WarningsList from './sections/WarningsList';
import s from './styles/dashboard.module.css';

const PurchaseResultsDashboard = () => {
  const params = useParams();
  const applicationId = params.id as string;
  const { data, isLoading, isError } = useGetPurchaseApplicationSummaryQuery({
    application_id: applicationId,
  });
  const {
    data: fxData,
    isFetching: fxLoading,
    isError: fxError,
  } = useFetchFxRatesQuery();
  const {
    data: hemData,
    isFetching: hemLoading,
    isError: hemError,
  } = useFetchHemBenchmarksQuery();
  const {
    data: taxData,
    isFetching: taxLoading,
    isError: taxError,
  } = useFetchIncomeTaxBracketsQuery();
  const {
    data: stampData,
    isFetching: stampLoading,
    isError: stampError,
  } = useFetchStampDutyBracketsQuery();
  const {
    data: settingsData,
    isFetching: settingsLoading,
    isError: settingsError,
  } = useFetchCalculationSettingsQuery();
  const {
    data: lenderData,
    isFetching: lenderLoading,
    isError: lenderError,
  } = useFetchBlueprintLendersQuery();

  const { result, runFailure } = useMemo<{
    result: ReturnType<typeof runPurchaseAssessment> | null;
    runFailure: string | null;
  }>(() => {
    if (
      !data ||
      !fxData?.rates ||
      !hemData ||
      !taxData ||
      !stampData ||
      !settingsData ||
      !lenderData
    ) {
      return { result: null, runFailure: null };
    }
    try {
      const input = mapSummaryToAssessmentInput(data, fxData.rates);
      if (!input) return { result: null, runFailure: null };
      const taxBrackets = normaliseTaxBrackets(taxData);
      const settings = normaliseCalculationSettings(settingsData);
      return {
        result: runPurchaseAssessment(input, {
          hemBenchmarks: hemData,
          taxBrackets,
          stampDutyBrackets: stampData,
          lenderPanel: lenderData,
          settings,
        }),
        runFailure: null,
      };
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Assessment computation failed.';
      return { result: null, runFailure: msg };
    }
  }, [data, fxData, hemData, taxData, stampData, settingsData, lenderData]);

  // Wait for all six live feeds before rendering.
  if (
    isLoading ||
    fxLoading ||
    hemLoading ||
    taxLoading ||
    stampLoading ||
    settingsLoading ||
    lenderLoading
  ) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.skeleton}>Drafting your brief…</div>
        </div>
      </div>
    );
  }

  if (fxError) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — exchange rates unavailable.</strong>
            <br />
            Live exchange-rate feed is unreachable. Refresh in a few minutes,
            or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (hemError || !hemData || hemData.length === 0) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — living-expense benchmarks unavailable.</strong>
            <br />
            HEM benchmark data could not be loaded. Refresh in a few minutes,
            or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (taxError || !taxData || taxData.length === 0) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — ATO tax brackets unavailable.</strong>
            <br />
            Income tax bracket data could not be loaded. Refresh in a few
            minutes, or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (
    stampError ||
    !stampData ||
    Object.keys(stampData).length === 0
  ) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — stamp-duty brackets unavailable.</strong>
            <br />
            State stamp-duty data could not be loaded. Refresh in a few
            minutes, or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (settingsError || !settingsData) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — calculation settings unavailable.</strong>
            <br />
            Engine configuration (rates, fees, growth assumptions) could not
            be loaded. Refresh in a few minutes, or contact ODIN support if
            this persists.
          </div>
        </div>
      </div>
    );
  }

  if (lenderError || !lenderData || lenderData.length === 0) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment — lender panel unavailable.</strong>
            <br />
            Lender policy data could not be loaded. Refresh in a few minutes,
            or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  // `runFailure` covers per-state missing-brackets (e.g. user picked SA but
  // API only carries NSW/VIC/QLD/WA) and other engine-side throws.
  if (runFailure) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>
            <strong>Cannot generate assessment.</strong>
            <br />
            {runFailure}
            <br />
            Refresh in a few minutes, or contact ODIN support if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (isError || !result) {
    return (
      <div className={s.report}>
        <div className={s.canvas}>
          <div className={s.empty}>Unable to load assessment result.</div>
        </div>
      </div>
    );
  }

  const isIncomplete =
    !result.input.purchase || result.input.primaryIncome.baseSalary === 0;
  const purchase = result.input.purchase;

  return (
    <div className={s.report}>
      <div className={s.canvas}>
        {isIncomplete && (
          <div className={`${s.block} ${s.incomplete}`}>
            <span className={s.incompleteTag}>Draft</span>
            <span className={s.incompleteBody}>
              Some key details (purchase price, deposit, or income) have not
              been submitted yet. Figures below are based on partial data and
              will update once the form is complete.
            </span>
          </div>
        )}


        <div className={s.block}>
          <BlueprintHeader result={result} applicationId={applicationId} />
        </div>

        {result.warnings.length > 0 && (
          <div className={s.block}>
            <WarningsList warnings={result.warnings} />
          </div>
        )}

        <section className={`${s.block} ${s.card}`}>
          <Chapter number="01" title="Position at a glance" />
          <KeyMetrics result={result} />
        </section>

        <section className={`${s.block} ${s.card}`}>
          <Chapter number="02" title="Profile assessment" />
          <StrengthsConsiderations rules={result.rules} />
          <TaxNote taxNote={result.rules.taxNote} />
        </section>

        <section className={`${s.block} ${s.card}`}>
          <Chapter number="03" title="Lender comparison" />
          <LendersPanel result={result} />
        </section>

        {result.cashflow && purchase && (
          <section className={`${s.block} ${s.card}`}>
            <Chapter number="04" title="Investment cashflow" />
            <CashflowCard
              cashflow={result.cashflow}
              propertyUse={purchase.propertyUse}
              propertyType={purchase.propertyType}
              state={purchase.propertyState}
              rentalYieldPct={purchase.rentalYieldPct}
              hasPurchase
            />
          </section>
        )}

        {result.capitalGains && purchase && (
          <section className={`${s.block} ${s.card}`}>
            <Chapter number="05" title="Capital growth horizon" />
            <CapitalGainsCard
              projection={result.capitalGains}
              purchasePrice={purchase.purchasePrice}
            />
          </section>
        )}

        <Disclaimer />
      </div>
    </div>
  );
};

export default PurchaseResultsDashboard;
